import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useTelemetrySource } from '../lib/api/source.tsx'
import type { DetectionQuery } from '../lib/api/admin.ts'
import { AthenaError } from '../lib/api/client.ts'
import { formatInt } from '../lib/format.ts'
import {
  RESPONSE_ACTIONS,
  RESPONSE_SHORTCUTS,
  RESPONSE_STATUSES,
  responseActionLabel,
  responseStatusPresentation,
  severityPresentation,
  summarize,
} from '../lib/detections/semantics.ts'
import { navigate, useSearchParams } from '../lib/router.tsx'
import { severityOfDetection } from '../lib/sse/parse.ts'
import type { DetectionAlert, DetectionSeverity } from '../lib/sse/types.ts'
import { PageHeader, Toolbar } from '../components/layout/PageHeader.tsx'
import { Button } from '../components/primitives/Button.tsx'
import { SelectField } from '../components/primitives/Field.tsx'
import { Panel } from '../components/primitives/Panel.tsx'
import { EmptyState, ErrorState, LoadingRow } from '../components/primitives/States.tsx'
import { TenantSelect } from '../components/logs/Directory.tsx'
import { DetectionDrawer } from '../components/detections/DetectionDrawer.tsx'
import { DetectionList } from '../components/detections/DetectionList.tsx'
import { ActivityChart, RankedBars, SeverityTiles, TacticMatrix } from '../components/detections/Panorama.tsx'

/** Uma página do servidor; o teto dele é 200. O panorama é sobre o que foi carregado. */
const PAGE_SIZE = 100

interface Filters {
  severity: DetectionSeverity | null
  ruleId: string | null
  endpointId: string | null
  tenantId: string | null
  /** Vocabulário aberto: vai ao servidor como veio; ele recusa o malformado (400). */
  responseAction: string | null
  responseStatus: string | null
  isolatedMachines: boolean
}

function readFilters(search: URLSearchParams): Filters {
  const text = (key: string) => {
    const value = search.get(key)?.trim()
    return value === undefined || value === '' ? null : value
  }
  return {
    severity: severityOfDetection(search.get('severity')),
    ruleId: text('ruleId'),
    endpointId: text('endpointId'),
    tenantId: text('tenantId'),
    responseAction: text('responseAction'),
    responseStatus: text('responseStatus'),
    isolatedMachines: search.get('isolatedMachines') === 'true',
  }
}

function filtersHref(filters: Filters): string {
  const search = new URLSearchParams()
  if (filters.severity !== null) search.set('severity', filters.severity)
  if (filters.ruleId !== null) search.set('ruleId', filters.ruleId)
  if (filters.endpointId !== null) search.set('endpointId', filters.endpointId)
  if (filters.tenantId !== null) search.set('tenantId', filters.tenantId)
  if (filters.responseAction !== null) search.set('responseAction', filters.responseAction)
  if (filters.responseStatus !== null) search.set('responseStatus', filters.responseStatus)
  if (filters.isolatedMachines) search.set('isolatedMachines', 'true')
  const query = search.toString()
  return query === '' ? '/deteccoes' : `/deteccoes?${query}`
}

/**
 * Detecções: a fila de alertas que o motor dos agentes emitiu, da frota inteira.
 *
 * Em cima, o PANORAMA da janela carregada — severidades, chegada no tempo, a
 * cadeia de ataque e quem mais disparou. Embaixo, a FILA, em ordem de chegada,
 * com a ficha completa a um clique. Os filtros vivem na URL: um recorte colado
 * num ticket abre igual para quem recebe.
 *
 * Não há atualização automática, e é de propósito: a consulta lê a tabela de
 * ingestão, e o contrato do servidor é "só por ação humana" (§ITelemetryQuery).
 * O botão Atualizar recarrega do topo.
 */
export function Detections() {
  const source = useTelemetrySource()
  const search = useSearchParams()
  // Na experiência de cliente não existe filtro de empresa: um `?tenantId=`
  // colado na URL é ignorado aqui e a fonte de cliente também o descartaria.
  const filters = useMemo(() => {
    const read = readFilters(search)
    return source.multiTenant ? read : { ...read, tenantId: null }
  }, [search, source])
  const [alerts, setAlerts] = useState<DetectionAlert[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>(null)
  const [nonce, setNonce] = useState(0)
  const [loadedAt, setLoadedAt] = useState<number | null>(null)
  const [selected, setSelected] = useState<DetectionAlert | null>(null)
  const cursorRef = useRef<string | null>(null)

  const params = useMemo<DetectionQuery>(
    () => ({
      severity: filters.severity ?? undefined,
      ruleId: filters.ruleId ?? undefined,
      endpointId: filters.endpointId ?? undefined,
      tenantId: filters.tenantId ?? undefined,
      responseAction: filters.responseAction ?? undefined,
      responseStatus: filters.responseStatus ?? undefined,
      isolatedMachines: filters.isolatedMachines,
      pageSize: PAGE_SIZE,
    }),
    [filters],
  )

  useEffect(() => {
    const controller = new AbortController()
    cursorRef.current = null
    setError(null)
    setLoading(true)
    source
      .detections(params, controller.signal)
      .then((page) => {
        cursorRef.current = page.next_cursor
        setAlerts(page.items)
        setHasMore(page.has_more)
        setLoading(false)
        setLoadedAt(Date.now())
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setAlerts([])
        setError(cause)
        setLoading(false)
      })
    return () => controller.abort()
  }, [source, params, nonce])

  const loadMore = useCallback(() => {
    if (loading) return
    setLoading(true)
    source
      .detections({ ...params, cursor: cursorRef.current })
      .then((page) => {
        cursorRef.current = page.next_cursor
        setAlerts((prev) => [...prev, ...page.items])
        setHasMore(page.has_more)
        setLoading(false)
      })
      .catch((cause: unknown) => {
        setError(cause)
        setLoading(false)
      })
  }, [source, loading, params])

  const summary = useMemo(() => summarize(alerts), [alerts])
  const setFilters = (next: Partial<Filters>) => navigate(filtersHref({ ...filters, ...next }))
  const anyFilter =
    filters.severity !== null ||
    filters.ruleId !== null ||
    filters.endpointId !== null ||
    filters.tenantId !== null ||
    filters.responseAction !== null ||
    filters.responseStatus !== null ||
    filters.isolatedMachines
  const cursorInvalid = error instanceof AthenaError && error.code === 'logs_cursor_invalid'
  const hostLabel =
    filters.endpointId === null
      ? null
      : (alerts.find((a) => a.row.endpoint_id === filters.endpointId)?.row.hostname ?? filters.endpointId.slice(0, 8))
  const ruleLabel =
    filters.ruleId === null ? null : (alerts.find((a) => a.rule_id === filters.ruleId)?.rule_name ?? filters.ruleId)

  return (
    <>
      <PageHeader
        title="Detecções"
        actions={
          <>
            {loadedAt !== null && (
              <span className="tnum text-micro text-fg-muted">
                carregado às {new Date(loadedAt).toLocaleTimeString('pt-BR')}
              </span>
            )}
            <Button size="sm" onClick={() => setNonce((n) => n + 1)} busy={loading && alerts.length === 0}>
              Atualizar
            </Button>
          </>
        }
      />

      <Toolbar>
        {source.multiTenant && (
          <TenantSelect value={filters.tenantId ?? ''} onChange={(value) => setFilters({ tenantId: value || null })} />
        )}
        <ResponseFilters filters={filters} onChange={setFilters} />
        <div className="flex flex-wrap items-center gap-1.5 self-end pb-1">
          {filters.severity !== null && (
            <FilterChip
              label="severidade"
              value={severityPresentation(filters.severity).label}
              onClear={() => setFilters({ severity: null })}
            />
          )}
          {ruleLabel !== null && (
            <FilterChip label="regra" value={ruleLabel} onClear={() => setFilters({ ruleId: null })} />
          )}
          {hostLabel !== null && (
            <FilterChip label="máquina" value={hostLabel} onClear={() => setFilters({ endpointId: null })} />
          )}
          {filters.responseAction !== null && (
            <FilterChip
              label="resposta"
              value={responseActionLabel(filters.responseAction)}
              onClear={() => setFilters({ responseAction: null })}
            />
          )}
          {filters.responseStatus !== null && (
            <FilterChip
              label="desfecho"
              value={responseStatusPresentation(filters.responseStatus).label}
              onClear={() => setFilters({ responseStatus: null })}
            />
          )}
          {filters.isolatedMachines && (
            <FilterChip label="máquinas" value="isoladas agora" onClear={() => setFilters({ isolatedMachines: false })} />
          )}
          {anyFilter && (
            <button
              type="button"
              onClick={() => navigate('/deteccoes')}
              className="text-micro uppercase tracking-label text-fg-muted underline hover:text-fg"
            >
              limpar filtros
            </button>
          )}
        </div>
      </Toolbar>

      <div className="min-h-0 flex-1 overflow-y-auto bg-canvas">
        {error !== null && alerts.length === 0 ? (
          <div className="p-3">
            <ErrorState error={error} onRetry={() => setNonce((n) => n + 1)} />
          </div>
        ) : alerts.length === 0 && loading ? (
          <LoadingRow label="Carregando a fila de alertas…" />
        ) : alerts.length === 0 ? (
          <EmptyState title={anyFilter ? 'Nenhum alerta neste recorte.' : 'Nenhum alerta recebido.'} />
        ) : (
          <div className="grid grid-cols-1 gap-px bg-line-subtle xl:grid-cols-12">
            <Panel
              title="Panorama"
              weight="hero"
              className="xl:col-span-7"
              meta={
                <span>
                  nos <span className="tnum text-fg-secondary">{formatInt(summary.total)}</span> alertas carregados
                  {hasMore && ' · há mais no servidor'}
                </span>
              }
            >
              <SeverityTiles
                summary={summary}
                active={filters.severity}
                onPick={(severity) => setFilters({ severity })}
              />
            </Panel>

            <Panel title="Chegada no tempo" className="xl:col-span-5" meta="recebimento no servidor">
              <ActivityChart alerts={alerts} />
              <Legend />
            </Panel>

            <Panel title="Cadeia de ataque · MITRE ATT&CK" className="xl:col-span-6">
              <TacticMatrix summary={summary} />
            </Panel>

            <Panel title="Regras que mais dispararam" className="xl:col-span-3">
              <RankedBars
                items={summary.rules}
                onPick={(item) => setFilters({ ruleId: item.key })}
                empty="Nenhuma regra."
              />
            </Panel>

            <Panel title="Máquinas com mais alertas" className="xl:col-span-3">
              <RankedBars
                items={summary.hosts}
                mono
                onPick={(item) => setFilters({ endpointId: item.key })}
                empty="Nenhuma máquina."
              />
            </Panel>

            <Panel
              title="Fila"
              className="xl:col-span-12"
              flush
              meta={
                <span>
                  <span className="tnum text-fg-secondary">{formatInt(alerts.length)}</span> carregados · mais novos
                  primeiro
                </span>
              }
            >
              <DetectionList
                alerts={alerts}
                selectedId={selected?.row.id ?? null}
                onOpen={(alert) => setSelected(alert)}
              />
              {error !== null && (
                <div className="px-3 py-2">
                  <ErrorState error={error} onRetry={cursorInvalid ? () => setNonce((n) => n + 1) : loadMore} />
                </div>
              )}
              <div className="flex items-center justify-center px-3 py-3">
                {hasMore ? (
                  <Button onClick={loadMore} busy={loading}>
                    Carregar mais antigos
                  </Button>
                ) : (
                  <span className="text-micro text-fg-muted">Fim da fila · {formatInt(alerts.length)} alertas</span>
                )}
              </div>
            </Panel>
          </div>
        )}
      </div>

      {selected !== null && (
        <DetectionDrawer
          alert={selected}
          onClose={() => setSelected(null)}
          onFilterRule={(ruleId) => setFilters({ ruleId })}
        />
      )}
    </>
  )
}

/**
 * Recortes pela resposta do agente. Os atalhos são os três que a triagem usa
 * todo dia; os seletores cobrem o resto. Ação e desfecho casam na MESMA
 * resposta do alerta — o servidor garante, a tela só repassa.
 */
function ResponseFilters({
  filters,
  onChange,
}: {
  filters: Filters
  onChange: (next: Partial<Filters>) => void
}) {
  // Valor vindo da URL fora do vocabulário conhecido continua selecionável.
  const options = (known: readonly string[], current: string | null, label: (v: string) => string) => [
    { value: '', label: 'qualquer' },
    ...[...known, ...(current !== null && !known.includes(current) ? [current] : [])].map((value) => ({
      value,
      label: label(value),
    })),
  ]

  return (
    <div className="flex flex-wrap items-end gap-2">
      <SelectField
        label="Resposta do agente"
        value={filters.responseAction ?? ''}
        onValueChange={(value) => onChange({ responseAction: value || null })}
        options={options(RESPONSE_ACTIONS, filters.responseAction, responseActionLabel)}
        className="w-44"
      />
      <SelectField
        label="Desfecho"
        value={filters.responseStatus ?? ''}
        onValueChange={(value) => onChange({ responseStatus: value || null })}
        options={options(RESPONSE_STATUSES, filters.responseStatus, (v) => responseStatusPresentation(v).label)}
        className="w-44"
      />
      <div className="flex flex-wrap items-center gap-1 pb-1" role="group" aria-label="Atalhos de resposta">
        {RESPONSE_SHORTCUTS.map((shortcut) => {
          const target: Partial<Filters> = {
            responseAction: shortcut.query.responseAction ?? null,
            responseStatus: shortcut.query.responseStatus ?? null,
            isolatedMachines: shortcut.query.isolatedMachines === true,
          }
          const active =
            filters.responseAction === target.responseAction &&
            filters.responseStatus === target.responseStatus &&
            filters.isolatedMachines === target.isolatedMachines
          return (
            <button
              key={shortcut.key}
              type="button"
              aria-pressed={active}
              title={shortcut.hint}
              onClick={() =>
                onChange(active ? { responseAction: null, responseStatus: null, isolatedMachines: false } : target)
              }
              className={`border px-1.5 py-px text-micro uppercase tracking-label ${
                active
                  ? shortcut.key === 'failed'
                    ? 'border-crit bg-crit/8 text-crit'
                    : 'border-accent bg-accent/8 text-accent'
                  : shortcut.key === 'failed'
                    ? 'border-crit/60 text-crit hover:bg-crit/8'
                    : 'border-line text-fg-secondary hover:text-fg'
              }`}
            >
              {shortcut.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function FilterChip({ label, value, onClear }: { label: string; value: string; onClear: () => void }) {
  return (
    <span className="inline-flex max-w-72 items-center gap-1 border border-accent bg-accent/8 px-1.5 py-px text-micro text-accent">
      <span className="uppercase tracking-label">{label}</span>
      <span className="truncate font-mono text-fg">{value}</span>
      <button type="button" onClick={onClear} aria-label={`Tirar o filtro de ${label}`} className="ml-0.5 hover:text-fg">
        ✕
      </button>
    </span>
  )
}

function Legend() {
  return (
    <ul className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-micro text-fg-muted">
      {(['critical', 'high', 'medium', 'low'] as const).map((severity) => {
        const p = severityPresentation(severity)
        return (
          <li key={severity} className="flex items-center gap-1">
            <span aria-hidden className={`inline-block size-2 ${p.fill}`} />
            {p.label}
          </li>
        )
      })}
    </ul>
  )
}
